<script setup>
import { computed } from 'vue';
import Avatar from 'next/avatar/Avatar.vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  cannedResponse: {
    type: Object,
    required: true,
  },
  index: {
    type: Number,
    required: true,
  },
  loading: {
    type: Object,
    default: () => ({}),
  },
});
const emit = defineEmits(['edit', 'delete']);

const { t } = useI18n();

const createdByName = computed(() => {
  const createdBy = props.cannedResponse.created_by;
  if (!createdBy) return '--';
  return createdBy.available_name ?? createdBy.name ?? createdBy.email ?? '';
});

const updatedByName = computed(() => {
  const updatedBy = props.cannedResponse.updated_by;
  if (!updatedBy) return '--';
  return updatedBy.available_name ?? updatedBy.name ?? updatedBy.email ?? '';
});

const visibilityLabel = computed(() => {
  const i18nKey =
    props.cannedResponse.visibility === 'global'
      ? 'CANNED_MGMT.VISIBILITY.GLOBAL.LABEL'
      : 'CANNED_MGMT.VISIBILITY.PERSONAL.LABEL';
  return t(i18nKey);
});

const openEditPopup = () => {
  emit('edit', props.cannedResponse);
};

const openDeletePopup = () => {
  emit('delete', props.cannedResponse, props.index);
};
</script>

<template>
  <tr>
    <td
      class="py-4 ltr:pr-4 rtl:pl-4 truncate max-w-xs font-medium"
      :title="cannedResponse.short_code"
    >
      {{ cannedResponse.short_code }}
    </td>
    <td
      class="py-4 ltr:pr-4 rtl:pl-4 max-w-md whitespace-normal break-words"
      :title="cannedResponse.content"
    >
      {{ cannedResponse.content }}
    </td>
    <td class="py-4 ltr:pr-4 rtl:pl-4">
      <div v-if="cannedResponse.created_by" class="flex items-center">
        <Avatar :name="createdByName" :size="24" rounded-full />
        <span class="mx-2">{{ createdByName }}</span>
      </div>
      <div v-else>--</div>
    </td>
    <td class="py-4 ltr:pr-4 rtl:pl-4">
      <div v-if="cannedResponse.updated_by" class="flex items-center">
        <Avatar :name="updatedByName" :size="24" rounded-full />
        <span class="mx-2">{{ updatedByName }}</span>
      </div>
      <div v-else>--</div>
    </td>
    <td class="py-4 ltr:pr-4 rtl:pl-4">{{ visibilityLabel }}</td>
    <td class="py-4 flex justify-end gap-1">
      <Button
        v-tooltip.top="$t('CANNED_MGMT.EDIT.BUTTON_TEXT')"
        icon="i-lucide-pen"
        slate
        xs
        faded
        @click="openEditPopup"
      />
      <Button
        v-tooltip.top="$t('CANNED_MGMT.DELETE.BUTTON_TEXT')"
        icon="i-lucide-trash-2"
        xs
        ruby
        faded
        :is-loading="loading[cannedResponse.id]"
        @click="openDeletePopup"
      />
    </td>
  </tr>
</template>
